import React, { useContext, useEffect } from 'react';
import QSightDataTable from '../../shared/QSightDataTable/QSightDataTable';
import { headers, UsageTransferTableProps, UsageTransferTableData } from './tableSetup';
import { UsageTransfer } from '@qsightpbi-integration/sdk';
import { Row, Col } from 'reactstrap';
import TransferOpportunitiesRow from './TransferOpportunitiesRow';
import QsightBasicMetricCardGroup from '../../shared/QSightMetricCard/QsightBasicMetricCardGroup';
import './UsageTransferTable.css';
import RowCountDropdown from '../../shared/QSightRowCountDropdown/RowCountDropdown';
import { ReportContext } from '../../shared/context/ReportProvider';
import { getQuantityLastYearValueFromUsageTransfer, getTransferFromQuantityFromUsageTransfer, getValueToExpireFromUsageTransfer, getDepartmentQtyFromUsageTransfer, ReportFilterContext } from './UsageTransferTableUtils';
import UsageTransferMetricsContainer from '../../shared/UsageTransferMetricsContainer/UsageTransferMetricsContainer';
import { useLocation } from 'react-router-dom';
const usdFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });


const UsageTransferTable: React.FC<UsageTransferTableProps> = ({ reportState }) => {
  const location = useLocation();
  const [rows, setRows] = React.useState<UsageTransferTableData[]>([]);
  const [expandedRows, setExpandedRows] = React.useState<string[]>([]);
  const reportContext = useContext(ReportContext);

  // Build table rows from reportState data
  useEffect(() => {
    const newRows: UsageTransferTableData[] = (reportState?.data ?? []).map((row, index) => {
      const productId = String(index); // Use index as consistent key since specific ID property is not available
      const rowExpanded = expandedRows.includes(String(index));

      return {
        id: productId,
        productInfo:
          <Row>
            <Col xs={12} md={5} >
              <span className='product-info-text'>{row.manufacturerName}</span>
            </Col>
            <Col xs={12} md={7} >
              <span className='product-info-text'>{row.productBrandName} - {row.productType}</span>
            </Col>
            <Col xs={12}>
              <span className='product-info-text'>{row.productDescription}</span>
            </Col>
            <Col xs={12} md={5} >
              <span className='product-info-text'>Item ID: <strong>{row.transferFromHospitalProductId ?? 'N/A'}</strong></span>
            </Col>
            <Col xs={12} md={7} >
              <span className='product-info-text'>Catalog #: <strong>{row.catalogNo ?? 'N/A'}</strong></span>
            </Col>
            <Col xs={12}>
              <span className='product-info-text'>At: <strong>{row.transferFromDepartmentName ?? 'N/A'}</strong>,&nbsp;
                {row.transferFromHospitalName ?? 'N/A'}</span>
            </Col>
          </Row>,
        quantity: <>
          <Row>
            <Col xs={3} className='quantity-value-col'>
              <span className='quantity-text'><strong>{row.parLevel}</strong></span>
            </Col>
            <Col xs={9} className='quantity-text-col'>
              <span className='quantity-text'>Par</span>
            </Col>
          </Row>
          <Row>
            <Col xs={3} className='quantity-value-col'>
              {(() => {
                const departmentQty = getDepartmentQtyFromUsageTransfer(row, reportContext?.reportFilter as ReportFilterContext);
                return (
                  <span className='quantity-text'>
                    <strong>{typeof departmentQty === 'number' ? departmentQty : 0}</strong>
                  </span>
                );
              })()}
            </Col>
            <Col xs={9} className='quantity-text-col'>
              <span className='quantity-text'>On Hand</span>
            </Col>
          </Row>
          <Row>
            <Col xs={3} className='quantity-value-col'>
              {(() => {
                const lastYearQty = getQuantityLastYearValueFromUsageTransfer(row, reportContext?.reportFilter as ReportFilterContext);
                return (
                  <span className='quantity-text'>
                    <strong>{typeof lastYearQty === 'number' ? lastYearQty : 0}</strong>
                  </span>
                );
              })()}
            </Col>
            <Col xs={9} className='quantity-text-col'>
              <span className='quantity-text'>Used in Last Yr</span>
            </Col>
          </Row>
          <Row>
            <Col xs={3} className='quantity-value-col'>
              {(() => {
                const mayExpireQty = getTransferFromQuantityFromUsageTransfer(row, reportContext?.reportFilter as ReportFilterContext);
                return (
                  <span className='quantity-text'>
                    <strong>{typeof mayExpireQty === 'number' ? mayExpireQty : 0}</strong>
                  </span>
                );
              })()}
            </Col>
            <Col xs={9} className='quantity-text-col'>
              <span className='quantity-text'>May Expire Unused</span>
            </Col>
          </Row>
          <Row>
            <Col xs={3} className='quantity-value-col'>
              <span className='quantity-text'><strong>{row.earliestExpiringInDays ?? 'N/A'}</strong></span>
            </Col>
            <Col xs={9} className='quantity-text-col'>
              <span className='quantity-text'>Days to Soonest Expiration</span>
            </Col>
          </Row>
        </>,
        valuePrice: <Row>
          <Col md={6} xs={12}>
            <Row>
              <Col xs={12}>
                {(() => {
                  const valueToExpire = getValueToExpireFromUsageTransfer(row, reportContext?.reportFilter as ReportFilterContext);
                  return (
                    <span className='value-price-text'>
                      Value: <strong>
                        {typeof valueToExpire === 'number'
                          ? usdFormatter.format(valueToExpire)
                          : 'N/A'}
                      </strong>
                    </span>
                  );
                })()}
              </Col>
              <Col xs={12}>
                <span className='value-price-text'>
                  Price: <strong>
                    {row.currentPriceEach !== undefined && row.currentPriceEach !== null
                      ? usdFormatter.format(row.currentPriceEach)
                      : 'N/A'}
                  </strong>
                </span>
              </Col>
            </Row>
          </Col>
          <Col md={6} xs={12}>
            <button className='faux-link' onClick={() => {
              setExpandedRows(prev =>
                prev.includes(productId)
                  ? prev.filter(id => id !== productId)
                  : [...prev, productId]
              );
            }}>Transfer Options <span className={`${rowExpanded ? '' : 'invert'}`}>^</span> </button>
          </Col>
        </Row>,
        extraInfo: rowExpanded ? <TransferOpportunitiesRow row={row} rowIndex={index} expandedRows={expandedRows} setExpandedRows={setExpandedRows} /> : null,
        // Add other transformations as needed
      };
    });
    setRows(newRows);
  }, [reportState?.data, expandedRows]);

  // Reset rows/expansion when a new API call starts
  useEffect(() => {
    if (reportState.loading) {
      setRows([]);
      setExpandedRows([]);
      // Ensure global loading reflects a new cycle
      reportContext?.setMetricCardsLoading?.(true);
    }
  }, [reportState.loading]);

  const isLoadingAll = reportState.loading || !!reportContext?.metricCardsLoading;

  return (
    <>
      <h1>Usage Transfer Details</h1>
      {isLoadingAll && (
        <Row>
          <Col xs={12} style={{ padding: '1rem' }}>
            <span>Loading report data…</span>
          </Col>
        </Row>
      )}
      {/* Always mount exactly one instance to avoid refetch loops */}
      <UsageTransferMetricsContainer key={location.pathname + location.search} />
      <RowCountDropdown />
      <QSightDataTable<UsageTransfer.Props, UsageTransferTableData>
        headers={headers}
        cssClass='usage-transfer-details-table'
        rows={rows}
        reportState={reportState}
      />
    </>
  );
};

export default UsageTransferTable;
